import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Award,
  Clock,
  Target,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Download,
  Calendar,
  BookOpen,
  Lightbulb,
  Zap,
  Loader,
  Eye,
  RefreshCw,
} from "lucide-react";
import { apiService } from "../services/api";
import styles from "./AssessmentReport.module.css";

const AssessmentReport = () => {
  const { reportId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [recommendedAssessments, setRecommendedAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllQuestions, setShowAllQuestions] = useState(false);

  const downloadReport = () => {
    if (!reportData) return;

    // Create a formatted text report
    let reportContent = `
╔════════════════════════════════════════════════════════════════╗
║           ASSESSMENT REPORT - PROSKILLNEST                     ║
╚════════════════════════════════════════════════════════════════╝

Assessment: ${reportData.assessmentTitle}
Skill: ${reportData.skillName || "N/A"}
Difficulty: ${reportData.difficulty || "N/A"}
Completed: ${new Date(reportData.completedAt).toLocaleString()}

════════════════════════════════════════════════════════════════

PERFORMANCE OVERVIEW
────────────────────────────────────────────────────────────────
Final Score:        ${reportData.score}%
Correct Answers:    ${reportData.correctAnswers}/${reportData.totalQuestions}
Accuracy Rate:      ${Math.round((reportData.correctAnswers / reportData.totalQuestions) * 100)}%
Time Spent:         ${reportData.duration ? `${reportData.duration} minutes` : "N/A"}
`;

    // Add comparison data
    if (reportData.comparison?.averageScore) {
      const percentile = Math.min(
        95,
        Math.max(
          5,
          50 + (reportData.score - reportData.comparison.averageScore) * 2,
        ),
      );
      reportContent += `
COMPARISON WITH OTHERS
────────────────────────────────────────────────────────────────
Your Score:         ${reportData.score}%
Average Score:      ${reportData.comparison.averageScore}%
Total Attempts:     ${reportData.comparison.totalAttempts || 0}
Your Percentile:    ${Math.round(percentile)}th
Performance:        ${reportData.score > reportData.comparison.averageScore ? "Above Average ↑" : reportData.score < reportData.comparison.averageScore ? "Below Average ↓" : "At Average"}
`;
    }

    // Add topic performance
    if (reportData.topicPerformance && reportData.topicPerformance.length > 0) {
      reportContent += `
TOPIC-WISE PERFORMANCE
────────────────────────────────────────────────────────────────
`;
      reportData.topicPerformance.forEach((topic) => {
        reportContent += `${topic.topic.padEnd(30)} ${topic.correct}/${topic.questions} (${topic.percentage}%)\n`;
      });
    }

    // Add AI insights
    if (reportData.personalizedInsights) {
      reportContent += `
════════════════════════════════════════════════════════════════

AI-POWERED PERSONALIZED INSIGHTS
────────────────────────────────────────────────────────────────
`;

      if (reportData.personalizedInsights.strengths?.length > 0) {
        reportContent += `\n✓ YOUR STRENGTHS:\n`;
        reportData.personalizedInsights.strengths.forEach((strength, idx) => {
          reportContent += `  ${idx + 1}. ${strength.text}\n`;
          if (strength.evidence) {
            reportContent += `     ${strength.evidence}\n`;
          }
        });
      }

      if (reportData.personalizedInsights.weaknesses?.length > 0) {
        reportContent += `\n⚠ AREAS FOR IMPROVEMENT:\n`;
        reportData.personalizedInsights.weaknesses.forEach((weakness, idx) => {
          reportContent += `  ${idx + 1}. ${weakness.text}`;
          if (weakness.severity) {
            reportContent += ` [${weakness.severity.toUpperCase()} PRIORITY]`;
          }
          reportContent += `\n`;
          if (weakness.evidence) {
            reportContent += `     ${weakness.evidence}\n`;
          }
        });
      }

      if (reportData.personalizedInsights.recommendations?.length > 0) {
        reportContent += `\n💡 SMART RECOMMENDATIONS:\n`;
        reportData.personalizedInsights.recommendations.forEach((rec, idx) => {
          reportContent += `  ${idx + 1}. ${rec.text}\n`;
          if (rec.reason) {
            reportContent += `     ${rec.reason}\n`;
          }
        });
      }
    }

    // Add performance trends
    if (reportData.performanceTrends) {
      reportContent += `
════════════════════════════════════════════════════════════════

YOUR LEARNING PROGRESS
────────────────────────────────────────────────────────────────
Learning Velocity:      ${reportData.performanceTrends.learningVelocity}
Consistency Score:      ${reportData.performanceTrends.consistencyScore}%
Overall Improvement:    ${reportData.performanceTrends.overallImprovement >= 0 ? "+" : ""}${reportData.performanceTrends.overallImprovement}%
Trend:                  ${reportData.performanceTrends.trend.replace(/_/g, " ")}
Total Attempts:         ${reportData.performanceTrends.totalAttempts}
`;
    }

    // Add course recommendations
    if (
      reportData.courseRecommendations &&
      reportData.courseRecommendations.length > 0
    ) {
      reportContent += `
════════════════════════════════════════════════════════════════

PERSONALIZED COURSE RECOMMENDATIONS
────────────────────────────────────────────────────────────────
`;
      reportData.courseRecommendations.forEach((course, idx) => {
        reportContent += `
${idx + 1}. ${course.title} [${course.priority.toUpperCase()} PRIORITY]
   Type: ${course.type}
   Skill: ${course.skillName}
   Time: ${course.estimatedTime}
   Expected Improvement: ${course.expectedImprovement}
   Reason: ${course.reason}
   ${course.url ? `URL: ${course.url}` : ""}
`;
      });
    }

    // Add question analysis
    if (reportData.questionAnalysis && reportData.questionAnalysis.length > 0) {
      reportContent += `
════════════════════════════════════════════════════════════════

DETAILED QUESTION ANALYSIS
────────────────────────────────────────────────────────────────
`;
      reportData.questionAnalysis.forEach((question, idx) => {
        reportContent += `
Question ${idx + 1}: ${question.isCorrect ? "✓ CORRECT" : "✗ INCORRECT"}
${question.question}

Your Answer: ${question.userAnswer || "Not answered"}
${!question.isCorrect && question.correctAnswer ? `Correct Answer: ${question.correctAnswer}` : ""}
${question.explanation ? `Explanation: ${question.explanation}` : ""}

────────────────────────────────────────────────────────────────
`;
      });
    }

    reportContent += `
════════════════════════════════════════════════════════════════

Report generated on: ${new Date().toLocaleString()}
ProSkillNest - Professional Skills Assessment Platform

════════════════════════════════════════════════════════════════
`;

    // Create and download the file
    const blob = new Blob([reportContent], {
      type: "text/plain;charset=utf-8",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Assessment_Report_${reportData.assessmentTitle.replace(/[^a-z0-9]/gi, "_")}_${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      // First fetch the report to get the category
      const reportResponse =
        await apiService.assessments.getAssessmentReport(reportId);

      if (!reportResponse) {
        throw new Error("Report not found");
      }

      setReportData(reportResponse);

      // Then fetch recommended assessments with the category filter
      const category = reportResponse.category;
      console.log("Fetching recommendations for category:", category);

      const recommendedResponse =
        await apiService.progress.getRecommendedAssessments(3, category);

      if (recommendedResponse?.recommendations) {
        setRecommendedAssessments(recommendedResponse.recommendations);
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(err.message || "Failed to load assessment report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const getPerformanceColor = (percentage) => {
    if (percentage >= 80) return "#10b981";
    if (percentage >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const getDifficultyColor = (difficulty) => {
    const diffMap = {
      beginner: "#10b981",
      easy: "#10b981",
      intermediate: "#f59e0b",
      medium: "#f59e0b",
      advanced: "#2563eb",
      hard: "#ef4444",
      expert: "#7c3aed",
    };
    return diffMap[difficulty?.toLowerCase()] || "#6b7280";
  };

  if (loading) {
    return (
      <div className={`${styles.assessmentReport} ${styles.loading}`}>
        <div className={styles.loadingContainer}>
          <Loader size={48} className={styles.spinner} />
          <h3>Loading Report...</h3>
          <p>Analyzing your performance</p>
        </div>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className={`${styles.assessmentReport} ${styles.error}`}>
        <div className={styles.errorContainer}>
          <AlertCircle size={48} />
          <h2>Report Not Available</h2>
          <p>
            {error || "The requested assessment report could not be found."}
          </p>
          <Link to="/progress" className="btn btn-primary">
            <ArrowLeft size={18} />
            Back to Progress
          </Link>
        </div>
      </div>
    );
  }

  const percentile = reportData.comparison?.averageScore
    ? Math.min(
        95,
        Math.max(
          5,
          50 + (reportData.score - reportData.comparison.averageScore) * 2,
        ),
      )
    : 50;

  return (
    <div className={styles.assessmentReport}>
      {/* Header */}
      <div className={styles.reportHeader}>
        <div className={styles.headerNavigation}>
          <Link to="/progress" className={styles.backLink}>
            <ArrowLeft size={20} />
            Back to Progress
          </Link>
        </div>

        <div className={styles.headerContent}>
          <div className={styles.assessmentInfo}>
            <h1>{reportData.assessmentTitle}</h1>
            <div className={styles.assessmentMeta}>
              {reportData.skillName && (
                <span className={styles.category}>{reportData.skillName}</span>
              )}
              {reportData.difficulty && (
                <span
                  className={styles.difficulty}
                  style={{ color: getDifficultyColor(reportData.difficulty) }}
                >
                  {reportData.difficulty}
                </span>
              )}
              <span className={styles.date}>
                <Calendar size={16} />
                {new Date(reportData.completedAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button className="btn btn-secondary" onClick={fetchReportData}>
              <RefreshCw size={18} />
              Refresh
            </button>
            <button className="btn btn-primary" onClick={downloadReport}>
              <Download size={18} />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Performance Overview */}
      <div className={`${styles.reportSection} ${styles.performanceOverview}`}>
        <h2>Performance Overview</h2>
        <div className={styles.performanceCards}>
          <div className={`${styles.performanceCard} ${styles.score}`}>
            <div className={styles.cardIcon}>
              <Award size={32} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardValue}>{reportData.score}%</div>
              <div className={styles.cardLabel}>Final Score</div>
              <div
                className={`${styles.cardComparison} ${
                  reportData.score > reportData.comparison?.averageScore
                    ? "positive"
                    : reportData.score < reportData.comparison?.averageScore
                      ? "negative"
                      : "neutral"
                }`}
              >
                {reportData.comparison?.averageScore ? (
                  reportData.score > reportData.comparison.averageScore ? (
                    <>
                      <TrendingUp size={16} />
                      {reportData.score - reportData.comparison.averageScore}%
                      above average
                    </>
                  ) : reportData.score < reportData.comparison.averageScore ? (
                    <>
                      <TrendingDown size={16} />
                      {reportData.comparison.averageScore - reportData.score}%
                      below average
                    </>
                  ) : (
                    "At average"
                  )
                ) : (
                  "No comparison data"
                )}
              </div>
            </div>
          </div>

          <div className={`${styles.performanceCard} ${styles.accuracy}`}>
            <div className={styles.cardIcon}>
              <Target size={32} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardValue}>
                {reportData.correctAnswers}/{reportData.totalQuestions}
              </div>
              <div className={styles.cardLabel}>Correct Answers</div>
              <div className={styles.cardComparison}>
                {Math.round(
                  (reportData.correctAnswers / reportData.totalQuestions) * 100,
                )}
                % accuracy
              </div>
            </div>
          </div>

          <div className={`${styles.performanceCard} ${styles.time}`}>
            <div className={styles.cardIcon}>
              <Clock size={32} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardValue}>
                {reportData.duration ? `${reportData.duration}m` : "N/A"}
              </div>
              <div className={styles.cardLabel}>Time Spent</div>
              <div className={styles.cardComparison}>
                {reportData.duration && reportData.comparison?.averageTime ? (
                  reportData.duration < reportData.comparison.averageTime ? (
                    <span className={styles.positive}>
                      <TrendingUp size={16} />
                      {reportData.comparison.averageTime - reportData.duration}m
                      faster
                    </span>
                  ) : (
                    <span className={styles.neutral}>
                      {reportData.duration - reportData.comparison.averageTime}m
                      slower
                    </span>
                  )
                ) : (
                  "Time not tracked"
                )}
              </div>
            </div>
          </div>

          <div className={`${styles.performanceCard} ${styles.percentile}`}>
            <div className={styles.cardIcon}>
              <BarChart3 size={32} />
            </div>
            <div className={styles.cardContent}>
              <div className={styles.cardValue}>{Math.round(percentile)}%</div>
              <div className={styles.cardLabel}>Percentile</div>
              <div className={styles.cardComparison}>
                Better than {Math.round(percentile)}% of test takers
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Breakdown */}
      <div className={`${styles.reportSection} ${styles.performanceBreakdown}`}>
        <h2>
          <BarChart3 size={24} />
          Performance Breakdown
        </h2>
        <div className={styles.breakdownGrid}>
          <div className={styles.breakdownCard}>
            <h3>Overall Statistics</h3>
            <div className={styles.breakdownStats}>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Questions</span>
                <span className={styles.statValue}>
                  {reportData.totalQuestions}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Correct Answers</span>
                <span className={`${styles.statValue} ${styles.correct}`}>
                  {reportData.correctAnswers}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Incorrect Answers</span>
                <span className={`${styles.statValue} ${styles.incorrect}`}>
                  {reportData.totalQuestions - reportData.correctAnswers}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Accuracy Rate</span>
                <span className={styles.statValue}>{reportData.score}%</span>
              </div>
            </div>
          </div>

          <div className={styles.breakdownCard}>
            <h3>Comparison with Others</h3>
            <div className={styles.breakdownStats}>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Your Score</span>
                <span className={styles.statValue}>{reportData.score}%</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Average Score</span>
                <span className={styles.statValue}>
                  {reportData.comparison?.averageScore || 0}%
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Attempts</span>
                <span className={styles.statValue}>
                  {reportData.comparison?.totalAttempts || 0}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Your Percentile</span>
                <span className={styles.statValue}>
                  {Math.round(percentile)}th
                </span>
              </div>
            </div>
          </div>

          <div className={styles.breakdownCard}>
            <h3>Performance by Difficulty</h3>
            <div className={styles.breakdownStats}>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>
                  <span
                    className={`${styles.difficultyDot} ${styles.easy}`}
                  ></span>
                  Easy Questions
                </span>
                <span className={styles.statValue}>
                  {reportData.performance?.excellent || 0} correct
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>
                  <span
                    className={`${styles.difficultyDot} ${styles.medium}`}
                  ></span>
                  Medium Questions
                </span>
                <span className={styles.statValue}>
                  {reportData.performance?.good || 0} correct
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>
                  <span
                    className={`${styles.difficultyDot} ${styles.hard}`}
                  ></span>
                  Hard Questions
                </span>
                <span className={styles.statValue}>
                  {reportData.performance?.average || 0} correct
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Topic Performance */}
      {reportData.topicPerformance &&
        reportData.topicPerformance.length > 0 && (
          <div className={`${styles.reportSection} ${styles.topicPerformance}`}>
            <h2>
              <Target size={24} />
              Topic-wise Performance
            </h2>
            <div className={styles.topicsGrid}>
              {reportData.topicPerformance.map((topic, index) => (
                <div key={index} className={styles.topicCard}>
                  <div className={styles.topicHeader}>
                    <h3>{topic.topic}</h3>
                    <span
                      className={styles.topicScoreBadge}
                      style={{
                        background: `${getPerformanceColor(topic.percentage)}20`,
                        color: getPerformanceColor(topic.percentage),
                      }}
                    >
                      {topic.percentage}%
                    </span>
                  </div>
                  <div className={styles.topicProgress}>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{
                          width: `${topic.percentage}%`,
                          background: getPerformanceColor(topic.percentage),
                        }}
                      ></div>
                    </div>
                    <div className={styles.topicStats}>
                      <span>
                        {topic.correct}/{topic.questions} correct
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* AI-Powered Personalized Insights */}
      {reportData.personalizedInsights && (
        <div className={`${styles.reportSection} ${styles.insights}`}>
          <h2>
            <Lightbulb size={24} />
            AI-Powered Personalized Insights
          </h2>
          <div className={styles.insightsGrid}>
            {reportData.personalizedInsights.strengths &&
              reportData.personalizedInsights.strengths.length > 0 && (
                <div className={`${styles.insightCard} ${styles.strengths}`}>
                  <div className={styles.insightHeader}>
                    <CheckCircle size={20} />
                    <h3>Your Strengths</h3>
                  </div>
                  <ul>
                    {reportData.personalizedInsights.strengths.map(
                      (strength, index) => (
                        <li key={index}>
                          <strong>{strength.text}</strong>
                          {strength.evidence && (
                            <div className={styles.insightEvidence}>
                              {strength.evidence}
                            </div>
                          )}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

            {reportData.personalizedInsights.weaknesses &&
              reportData.personalizedInsights.weaknesses.length > 0 && (
                <div className={`${styles.insightCard} ${styles.weaknesses}`}>
                  <div className={styles.insightHeader}>
                    <AlertCircle size={20} />
                    <h3>Areas for Improvement</h3>
                  </div>
                  <ul>
                    {reportData.personalizedInsights.weaknesses.map(
                      (weakness, index) => (
                        <li key={index}>
                          <strong>{weakness.text}</strong>
                          {weakness.evidence && (
                            <div className={styles.insightEvidence}>
                              {weakness.evidence}
                            </div>
                          )}
                          {weakness.severity && (
                            <span
                              className={`${styles.severityBadge} ${weakness.severity}`}
                            >
                              {weakness.severity} priority
                            </span>
                          )}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

            {reportData.personalizedInsights.recommendations &&
              reportData.personalizedInsights.recommendations.length > 0 && (
                <div
                  className={`${styles.insightCard} ${styles.recommendations}`}
                >
                  <div className={styles.insightHeader}>
                    <BookOpen size={20} />
                    <h3>Smart Recommendations</h3>
                  </div>
                  <ul>
                    {reportData.personalizedInsights.recommendations.map(
                      (rec, index) => (
                        <li key={index}>
                          <strong>{rec.text}</strong>
                          {rec.reason && (
                            <div className={styles.insightEvidence}>
                              {rec.reason}
                            </div>
                          )}
                          {rec.priority && (
                            <span
                              className={`${styles.priorityBadge} ${rec.priority}`}
                            >
                              {rec.priority} priority
                            </span>
                          )}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Performance Trends */}
      {reportData.performanceTrends && (
        <div className={`${styles.reportSection} ${styles.performanceTrends}`}>
          <h2>
            <TrendingUp size={24} />
            Your Learning Progress
          </h2>
          <div className={styles.trendsGrid}>
            <div className={styles.trendCard}>
              <div className={styles.trendIcon}>
                <Zap size={32} />
              </div>
              <div className={styles.trendContent}>
                <h3>Learning Velocity</h3>
                <div className={styles.trendValue}>
                  {reportData.performanceTrends.learningVelocity}
                </div>
                <p>{reportData.performanceTrends.velocityDescription}</p>
              </div>
            </div>

            <div className={styles.trendCard}>
              <div className={styles.trendIcon}>
                <Target size={32} />
              </div>
              <div className={styles.trendContent}>
                <h3>Consistency Score</h3>
                <div className={styles.trendValue}>
                  {reportData.performanceTrends.consistencyScore}%
                </div>
                <p>
                  Performance consistency across{" "}
                  {reportData.performanceTrends.totalAttempts} attempts
                </p>
              </div>
            </div>

            <div className={styles.trendCard}>
              <div className={styles.trendIcon}>
                {reportData.performanceTrends.overallImprovement >= 0 ? (
                  <TrendingUp size={32} />
                ) : (
                  <TrendingDown size={32} />
                )}
              </div>
              <div className={styles.trendContent}>
                <h3>Overall Improvement</h3>
                <div
                  className={`${styles.trendValue} ${reportData.performanceTrends.overallImprovement >= 0 ? "positive" : "negative"}`}
                >
                  {reportData.performanceTrends.overallImprovement >= 0
                    ? "+"
                    : ""}
                  {reportData.performanceTrends.overallImprovement}%
                </div>
                <p>
                  Trend: {reportData.performanceTrends.trend.replace(/_/g, " ")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Recommendations */}
      {reportData.courseRecommendations &&
        reportData.courseRecommendations.length > 0 && (
          <div
            className={`${styles.reportSection} ${styles.courseRecommendations}`}
          >
            <h2>
              <BookOpen size={24} />
              Personalized Course Recommendations
            </h2>
            <p className={styles.sectionDescription}>
              Based on your performance analysis, these courses will help you
              improve:
            </p>
            <div className={styles.coursesGrid}>
              {reportData.courseRecommendations.map((course, index) => (
                <div key={index} className={styles.courseCard}>
                  <div className={styles.courseHeader}>
                    <div
                      className={`${styles.coursePriority} ${course.priority}`}
                    >
                      {course.priority} priority
                    </div>
                    <span className={styles.courseType}>{course.type}</span>
                  </div>
                  <h3>{course.title}</h3>
                  <p className={styles.courseReason}>{course.reason}</p>
                  <div className={styles.courseMeta}>
                    <span className={styles.courseSkill}>
                      {course.skillName}
                    </span>
                    <span className={styles.courseTime}>
                      <Clock size={14} />
                      {course.estimatedTime}
                    </span>
                    <span
                      className={`${styles.courseImprovement} ${styles.positive}`}
                    >
                      {course.expectedImprovement} expected
                    </span>
                  </div>
                  {course.url && (
                    <a
                      href={course.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-sm"
                    >
                      Start Learning
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Recommended Assessments */}
      {recommendedAssessments.length > 0 && (
        <div
          className={`${styles.reportSection} ${styles.recommendedAssessments}`}
        >
          <h2>
            <Zap size={24} />
            Recommended Next Steps
          </h2>
          <p className={styles.sectionDescription}>
            Based on your performance, we recommend these assessments to
            continue your learning journey:
          </p>
          <div className={styles.recommendationsGrid}>
            {recommendedAssessments.map((assessment, index) => (
              <div key={index} className={styles.recommendationCard}>
                <div className={styles.recommendationHeader}>
                  <div
                    className={styles.recommendationIcon}
                    style={{
                      background: `${getDifficultyColor(assessment.difficulty)}20`,
                      color: getDifficultyColor(assessment.difficulty),
                    }}
                  >
                    <BookOpen size={24} />
                  </div>
                  <span
                    className={styles.difficultyBadge}
                    style={{
                      background: `${getDifficultyColor(assessment.difficulty)}20`,
                      color: getDifficultyColor(assessment.difficulty),
                    }}
                  >
                    {assessment.difficulty}
                  </span>
                </div>
                <div className={styles.recommendationContent}>
                  <h3>{assessment.name}</h3>
                  <p>{assessment.description}</p>
                  <div className={styles.recommendationMeta}>
                    {assessment.skillName && (
                      <span className={styles.skillTag}>
                        {assessment.skillName}
                      </span>
                    )}
                    <span className={styles.duration}>
                      <Clock size={14} />
                      {assessment.duration}
                    </span>
                  </div>
                  {assessment.userAttemptCount > 0 && (
                    <div className={styles.previousAttempts}>
                      Previous: {assessment.userAttemptCount} attempt
                      {assessment.userAttemptCount !== 1 ? "s" : ""} •{" "}
                      {assessment.userBestScore}% best
                    </div>
                  )}
                </div>
                <a
                  href={`/assessment/${assessment.id}`}
                  className="btn btn-primary btn-block"
                >
                  <Eye size={16} />
                  {assessment.userAttemptCount === 0 ? "Start" : "Retry"}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Question Analysis */}
      {reportData.questionAnalysis &&
        reportData.questionAnalysis.length > 0 && (
          <div className={`${styles.reportSection} ${styles.questionAnalysis}`}>
            <h2>
              <Target size={24} />
              Question Analysis
            </h2>
            <div className={styles.questionsList}>
              {(showAllQuestions
                ? reportData.questionAnalysis
                : reportData.questionAnalysis.slice(0, 5)
              ).map((question, index) => (
                <div
                  key={question.id}
                  className={`${styles.questionItem} ${question.isCorrect ? "correct" : "incorrect"}`}
                >
                  <div className={styles.questionHeader}>
                    <div className={styles.questionStatus}>
                      {question.isCorrect ? (
                        <CheckCircle size={20} className={styles.correctIcon} />
                      ) : (
                        <XCircle size={20} className={styles.incorrectIcon} />
                      )}
                    </div>
                    <div className={styles.questionInfo}>
                      <h4>Question {index + 1}</h4>
                      <p className={styles.questionText}>{question.question}</p>
                    </div>
                  </div>

                  <div className={styles.questionDetails}>
                    <div className={styles.answerComparison}>
                      <div className={styles.userAnswer}>
                        <strong>Your Answer:</strong>
                        <p>{question.userAnswer || "Not answered"}</p>
                      </div>
                      {!question.isCorrect && question.correctAnswer && (
                        <div className={styles.correctAnswer}>
                          <strong>Correct Answer:</strong>
                          <p>{question.correctAnswer}</p>
                        </div>
                      )}
                    </div>
                    {question.explanation && (
                      <div className={styles.explanation}>
                        <strong>Explanation:</strong>
                        <p>{question.explanation}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {reportData.questionAnalysis.length > 5 && (
              <div className={styles.viewAllQuestions}>
                <button
                  className="btn btn-outline"
                  onClick={() => setShowAllQuestions(!showAllQuestions)}
                >
                  {showAllQuestions
                    ? "Show Less"
                    : `View All ${reportData.totalQuestions} Questions`}
                </button>
              </div>
            )}
          </div>
        )}

      {/* Next Steps */}
      <div className={`${styles.reportSection} ${styles.nextSteps}`}>
        <h2>Continue Learning</h2>
        <div className={styles.nextStepsGrid}>
          <Link to="/assessments" className={styles.nextStepCard}>
            <Award size={32} />
            <h3>Take Another Assessment</h3>
            <p>Continue improving your skills</p>
          </Link>

          <Link to="/learning-paths" className={styles.nextStepCard}>
            <BookOpen size={32} />
            <h3>Explore Learning Paths</h3>
            <p>Find structured learning resources</p>
          </Link>

          <Link to="/progress" className={styles.nextStepCard}>
            <BarChart3 size={32} />
            <h3>View Progress</h3>
            <p>Track your improvement over time</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AssessmentReport;
